import {
  WebGLRenderer,
  WebGLRenderTarget,
  ACESFilmicToneMapping,
  sRGBEncoding,
  TextureLoader,
  AdditiveBlending,
  Texture,
  Color,
  PCFSoftShadowMap,
  Vector2
} from 'three'

import {
  EffectComposer,
  RenderPass,
  EffectPass,
  DepthOfFieldEffect,
  SSAOEffect,
  BloomEffect,
  SelectiveBloomEffect,
  SMAAEffect,
  SMAAPreset,
  NormalPass,
  BlendFunction,
  KernelSize,
  MaskPass,
  EdgeDetectionMode,
  ClearPass,
  ClearMaskPass,
  TextureEffect,
  SavePass
} from 'postprocessing'

export class Renderer {
  constructor (scene, camera, sceneScreen, scenePerso) {
    this.scene = scene
    this.sceneScreen = sceneScreen
    this.scenePerso = scenePerso
    this.camera = camera

    this.resize = this.resize.bind(this)
    this.render = this.render.bind(this)

    this.webGLRenderer = this.createRenderer()
    this.webGLRenderer.shadowMap.enabled = false
    this.persoWebGLRenderer = this.createRenderer()
    this.persoWebGLRenderer.setClearColor(0x333333, 0)

    this.smaaEffect = new SMAAEffect(
      Object.assign(new Image(), { src: SMAAEffect.searchImageDataURL }),
      Object.assign(new Image(), { src: SMAAEffect.areaImageDataURL }),
      SMAAPreset.HIGH,
      EdgeDetectionMode.COLOR
    )

    this.bloomEffect = new BloomEffect({
      intensity: .7,
      resolutionScale: .35,
      luminanceThreshold: .2,
      luminanceSmoothing: .2,
      kernelSize: KernelSize.MEDIUM
    })
    this.bloomEffect.blurPass.scale = .35

    this.basicWebGLRenderer = new WebGLRenderer({
      stencil: false,
      alpha: true
    })
    this.basicWebGLRenderer.setClearColor(0x333333, 0)
    this.basicWebGLRenderer.setPixelRatio( window.devicePixelRatio >= 2 ? 2 : 1 )

    // BASIC EWBGL RENDERER
    this.effectPass2 = new EffectPass(this.camera, this.smaaEffect)
    this.effectPass2.renderToScreen = true
    this.composer2 = new EffectComposer(this.basicWebGLRenderer, {
      stencilBuffer: false,
      depthBuffer: true
    })
    this.renderPass2 = new RenderPass(this.sceneScreen, this.camera)
    this.renderPass2.renderToScreen = false
    this.composer2.addPass(this.renderPass2)
    this.composer2.addPass(this.effectPass2)

    // MAIN RENDERER
    this.renderPass = new RenderPass(this.scene, this.camera)
    this.renderPass.renderToScreen = false
    this.effectPass = new EffectPass(this.camera, this.smaaEffect, this.bloomEffect)
    this.effectPass.renderToScreen = true
    this.composer = new EffectComposer(this.webGLRenderer, {
      stencilBuffer: false,
      depthBuffer: true
    })
    this.composer.addPass(this.renderPass)
    this.composer.addPass(this.effectPass)

    // AVATAR RENDERER
    this.composer3 = new EffectComposer(this.persoWebGLRenderer, {
      stencilBuffer: false,
      depthBuffer: true
    })
    this.renderPass3 = new RenderPass(this.scenePerso, this.camera)
    this.renderPass3.renderToScreen = false
    this.effectPass3 = new EffectPass(this.camera, this.smaaEffect)
    this.effectPass3.renderToScreen = true
    this.composer3.addPass(this.renderPass3)
    this.composer3.addPass(this.effectPass3)
  }

  get canvas () {
    return this.webGLRenderer.domElement
  }
  get canvas2 () {
    return this.basicWebGLRenderer.domElement
  }
  get canvas3 () {
    return this.persoWebGLRenderer.domElement
  }

  resize ({width, height}) {
    this.webGLRenderer.setSize(width, height)
    this.basicWebGLRenderer.setSize(width, height)
    this.persoWebGLRenderer.setSize(width, height)
    this.composer.setSize(width, height)
    this.composer2.setSize(width, height)
    this.composer3.setSize(width, height)

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
  }

  render ({ elapsed }) {
    this.composer.render(elapsed)
    this.composer2.render(elapsed)
    this.composer3.render(elapsed)
  }

  createRenderer () {
    const renderer = new WebGLRenderer({
      antialias: false,
      alpha: true,
      depth: false
    })

    renderer.setClearColor(0x333333, 1)
    renderer.outputEncoding = sRGBEncoding
    // renderer.toneMapping = ACESFilmicToneMapping
    renderer.gammaFactor = 2.2
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = PCFSoftShadowMap
    renderer.setPixelRatio( window.devicePixelRatio > 2 ? 2 : 1 )

    return renderer
  }

  set color (color) {
    this.webGLRenderer.setClearColor(new Color(color), 1)
  }

  destroy () {
    this.composer.dispose()
  }
}
